import { useState, useEffect, Fragment } from 'react';
import intl from 'react-intl-universal';
import { useSnackbar } from 'notistack';
import Menu from '@mui/material/Menu';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ConstructionIcon from '@mui/icons-material/Construction';
import ErrorIcon from '@mui/icons-material/Error';
import pxToRem from 'assets/theme/functions/pxToRem';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';
import { zIndexValues } from 'utils/styles';

import { formatBytes, calculateRemainingTime } from 'utils/misc';
import { useMaterialUIController } from 'context';
import {
  useUploadMissionsContextController,
  removeMissionFromContext,
  abortUpload,
} from 'context/NewIssueContext';
import UploadMissionCard from './Card';
import { getBadge, generateMissionDetailsModalContents } from './util';
import { MISSION, MISSION_STATUS } from 'shared/constants/uploadAttachments';

import logger from 'utils/logger';

const log = logger.createLogger('UploadMissionsMenu');

const UploadMissionsMenu = () => {
  // Contexts
  const [controller] = useMaterialUIController();
  const { enqueueSnackbar } = useSnackbar();
  const { darkMode } = controller;
  const [controllerUploadMissions, dispatchUploadMissions] =
    useUploadMissionsContextController();
  // States
  const [anchorEl, setAnchorEl] = useState(null);

  const [detailModalIsOpen, setDetailModalIsOpen] = useState(false);
  const [selectedMission, setSelectedMission] = useState({});

  const [abortUploadWarningModalIsOpen, setAbortUploadWarningModalIsOpen] =
    useState(false);
  const [missionIDToBeAborted, setMissionIDToBeAborted] = useState('');

  const missions = controllerUploadMissions.missions;
  const numMissions = Object.values(missions).length;

  const missionsNotDoneYet = Object.values(missions).filter(
    (x) =>
      x.status !== MISSION_STATUS.PROCESS_COMPLETE &&
      x.status !== MISSION_STATUS.UPLOAD_ABORTED
  );

  const numMissionsNotDoneYet = missionsNotDoneYet.length;

  useEffect(() => {
    //! Shows popup to alert user if they try to refresh or leave the actual page when uploading
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [controllerUploadMissions.newIssueIdentifierId]);

  const alertUser = (e) => {
    const ongoings = Object.values(controllerUploadMissions.missions).filter(
      (x) =>
        x.status === MISSION_STATUS.UPLOADING ||
        x.status === MISSION_STATUS.PROCESSING
    );
    const numOngoing = ongoings.length;
    if (numOngoing > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  // Add this effect to ensure anchorEl is reset when missions change
  useEffect(() => {
    // Reset anchorEl when missions change to prevent unwanted menu opening
    if (numMissions === 0) {
      setAnchorEl(null);
    }
  }, [numMissions]);

  useEffect(() => {
    if (controllerUploadMissions.newIssueIdentifierId) {
      const missionId = controllerUploadMissions.newIssueIdentifierId;
      const mission = controllerUploadMissions.missions[missionId];
      if (mission) {
        setDetailModalIsOpen(true);
        setSelectedMission(mission);
      }
    }
  }, [controllerUploadMissions.newIssueIdentifierId]);

  useEffect(() => {
    if (controllerUploadMissions.newAttachmentsMissionId) {
      const missionId = controllerUploadMissions.newAttachmentsMissionId;
      const mission = controllerUploadMissions.missions[missionId];
      setDetailModalIsOpen(true);
      setSelectedMission(mission);
    }
  }, [controllerUploadMissions.newAttachmentsMissionId]);

  //! Will check the missing for processing state, if so then close the abort modal
  useEffect(() => {
    if (selectedMission.status === MISSION_STATUS.PROCESSING) {
      if (abortUploadWarningModalIsOpen) {
        enqueueSnackbar(intl.get('upload_mission_snackbar_alert_processing'), {
          variant: 'info',
          autoHideDuration: 3000,
        });
        setAbortUploadWarningModalIsOpen(false);
      }
    }
  }, [controllerUploadMissions.missions]);

  useEffect(() => {
    if (controllerUploadMissions.newCompletedIssue) {
      enqueueSnackbar(intl.get('upload_mission_snackbar_alert_done'), {
        variant: 'success',
        autoHideDuration: 3000,
      });
    }
  }, [controllerUploadMissions.newCompletedIssue]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (missionId) => {
    setSelectedMission(missions[missionId]);
    setDetailModalIsOpen(true);
  };

  const handleMissionAbortWarningModalConfirm = () => {
    abortUpload(dispatchUploadMissions, { missionId: missionIDToBeAborted });
    setAbortUploadWarningModalIsOpen(false);
  };

  const handleMissionAbortWarningModalCancel = () => {
    setAbortUploadWarningModalIsOpen(false);
  };

  const handleDetailModalClose = () => {
    // if mission is already done, remove it from context;
    if (selectedMission.status === MISSION_STATUS.PROCESS_COMPLETE) {
      removeMissionFromContext(dispatchUploadMissions, {
        missionId: selectedMission.issueIdentifierId,
      });
    }
    setSelectedMission({});
    setDetailModalIsOpen(false);
    setAnchorEl(null);
  };

  const setMissionToBeRemovedFromList = (missionId) => {
    removeMissionFromContext(dispatchUploadMissions, {
      missionId: missionId,
    });
  };

  const renderMenuItems = () => {
    const missionItemsList = Object.entries(missions).map(
      ([_, value], index) => {
        const mission = value;
        const id =
          mission.type === MISSION.ISSUE
            ? mission.issueIdentifierId
            : mission.missionId;
        return (
          <MenuItem
            key={index}
            value={id}
            sx={{ padding: 0 }}
            onClick={() => handleMenuItemClick(id)}>
            <UploadMissionCard
              mission={mission}
              closeMenu={handleMenuClose}
              setAbortUploadWarningModalIsOpen={
                setAbortUploadWarningModalIsOpen
              }
              setMissionIDToBeAborted={setMissionIDToBeAborted}
              setMissionToBeRemoved={setMissionToBeRemovedFromList}
            />
          </MenuItem>
        );
      }
    );

    return missionItemsList;
  };

  // Render the upload missions menu bottom corner
  const renderMenu = () => (
    <Menu
      id="upload-mission-menu"
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          style: {
            width: 500,
            maxWidth: '100%',
            padding: 0,
          },
        },
      }}
      disableScrollLock={true}
      disableEnforceFocus={true}>
      <MenuList sx={{ padding: 0 }}>{renderMenuItems()}</MenuList>
    </Menu>
  );

  const handleAbortButtonClick = (e) => {
    e.stopPropagation();
    const id =
      selectedMission.type === MISSION.ISSUE
        ? selectedMission.issueIdentifierId
        : selectedMission.missionId;
    setMissionIDToBeAborted(id);
    setAbortUploadWarningModalIsOpen(true);
  };

  if (numMissions === 0) {
    return <></>;
  }
  //! TO check changed the location of the button
  return (
    <Fragment>
      <MDButton
        right="200rem"
        bottom="200rem"
        variant="gradient"
        color="info"
        style={{
          color: 'white',
          fontSize: pxToRem(18),
          width: '400px',
          display: 'flex',
          position: 'fixed',
          top: 70,
          right: 30,
        }}
        onClick={handleMenuClick}>
        {`${numMissionsNotDoneYet} ${intl.get('upload_mission_menu_bar_text')}`}
      </MDButton>

      {renderMenu()}

      {/* Modal of misson details*/}
      {/* Irregular */}
      <Modal
        data-testid="upload-mission-popup-window"
        open={detailModalIsOpen}
        onClose={handleDetailModalClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndexValues.modal3,
        }}>
        <Card sx={{ maxHeight: '70%', width: '60%', margin: 'auto' }}>
          <Grid container justifyContent="start">
            <Grid>
              <MDBox
                mt={3}
                mb={2}
                mx={6}
                display="flex"
                alignItems="center"
                data-testid="modal-upload-mission-status">
                {selectedMission &&
                  selectedMission.status !== MISSION_STATUS.UPLOADING &&
                  getBadge(selectedMission.status, 'lg')}
              </MDBox>
            </Grid>
          </Grid>
          <MDBox
            py={0}
            px={3}
            mt={0}
            mx={3}
            sx={{
              flexDirection: 'column',
              overflow: 'hidden',
              overflowY: 'scroll',
              maxHeight: '70%',
              overFlowY: 'auto',
            }}>
            {detailModalIsOpen &&
              generateMissionDetailsModalContents(selectedMission, darkMode)}
          </MDBox>
          <MDBox py={0} px={3} mt={0} mx={3}>
            {selectedMission.status === MISSION_STATUS.UPLOADING ? (
              <MDBox mt={1} mb={1} data-testid="upload-section">
                <Stack direction="row" spacing={1} alignItems="center">
                  <MDTypography variant="body1" color="text" fontWeight="bold">
                    {`${intl.get('upload_mission_menu_item_uploading_file')}: `}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    color="text"
                    style={{ whiteSpace: 'normal' }}
                    sx={{ width: '70%' }}>
                    {`${selectedMission.uploadingFileName}`}
                  </MDTypography>
                </Stack>
                <MDBox display="flex" alignItems="center">
                  <MDBox mt={0.5} width="100%">
                    <MDProgress
                      variant="gradient"
                      value={selectedMission.uploadingProgress}
                      color="info"
                    />
                  </MDBox>
                  <MDTypography variant="body3" ml={1}>
                    {`${Math.round(selectedMission.uploadingProgress)}%`}
                  </MDTypography>
                </MDBox>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center">
                  <MDTypography variant="body3">
                    {`${intl.get(
                      'upload_mission_menu_item_upload_speed'
                    )}: ${formatBytes(selectedMission.uploadingSpeed)}/Sec`}
                  </MDTypography>
                  <MDTypography variant="body3" mr={1}>
                    {`${intl.get(
                      'upload_mission_menu_item_upload_time_remaining'
                    )}: ${calculateRemainingTime(
                      selectedMission.batchTotalBytes,
                      selectedMission.uploadedBytes,
                      selectedMission.uploadingSpeed
                    )}`}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ) : (
              <></>
            )}

            {selectedMission.status === MISSION_STATUS.PROCESSING ? (
              <MDBox mt={1} display="flex" alignItems="center">
                <HourglassTopIcon
                  color="info"
                  fontSize="medium"
                  sx={{ mx: 1 }}
                />
                <MDTypography fontWeight="bold" color="info">
                  {`${intl.get('upload_mission_menu_item_message_processing')}`}
                </MDTypography>
              </MDBox>
            ) : (
              <></>
            )}

            {selectedMission.status === MISSION_STATUS.UPLOAD_FAILED ? (
              <MDBox mt={1} display="flex" alignItems="center">
                <ErrorIcon color="error" fontSize="medium" sx={{ mx: 1 }} />
                <MDTypography fontWeight="bold" color="error">
                  {`${intl.get(
                    'upload_mission_menu_item_message_upload_failed'
                  )}`}
                </MDTypography>
              </MDBox>
            ) : (
              <></>
            )}

            {selectedMission.status === MISSION_STATUS.PROCESS_FAILED ? (
              <MDBox mt={1} display="flex" alignItems="center">
                <ConstructionIcon
                  color="error"
                  fontSize="medium"
                  sx={{ mx: 1 }}
                />
                <MDTypography fontWeight="bold" color="error">
                  {`${intl.get(
                    'upload_mission_menu_item_message_process_failed'
                  )}`}
                </MDTypography>
              </MDBox>
            ) : (
              <></>
            )}

            <Grid
              container
              spacing={1}
              justifyContent="space-around"
              alignItems="center">
              <Grid>
                <MDButton
                  data-testid="upload_mission_menu_item_details_modal_close_button"
                  variant="gradient"
                  color="info"
                  style={{
                    color: 'white',
                    fontSize: pxToRem(18),
                    marginTop: '1px',
                    marginBottom: '15px',
                    width: '150px',
                  }}
                  onClick={handleDetailModalClose}>
                  {intl.get(
                    'upload_mission_menu_item_details_modal_close_button'
                  )}
                </MDButton>
              </Grid>
              {selectedMission.status === MISSION_STATUS.UPLOADING && (
                <Grid display="flex" justifyContent="flex-end">
                  <MDButton
                    data-testid="upload_mission_menu_item_details_modal_abort_button"
                    size="medium"
                    width="50px"
                    variant="contained"
                    color="error"
                    sx={{ my: 2, mx: 6 }}
                    onClick={handleAbortButtonClick}>
                    {intl.get('upload_mission_menu_item_abort')}
                  </MDButton>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>
      </Modal>

      {/* Modal delete confirmation */}
      <Modal
        data-testid="upload_mission_modal_delete_confirmation"
        // disablebackdropclick
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Prevent closing on backdrop click
          }
        }}
        open={abortUploadWarningModalIsOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'scroll',
          zIndex: zIndexValues.modal3 + 1,
        }}>
        <Card sx={{ margin: 4, marginTop: 3, width: 800 }}>
          <MDBox py={3} px={3} mt={3} mx={3}>
            <div>
              <MDTypography variant="h4" mb={1}>
                {intl.get('upload_mission_menu_item_abort_question')}
              </MDTypography>
              <MDTypography mb={4}>
                {intl.get('upload_mission_menu_item_abort_warning')}
              </MDTypography>
              <MDTypography mb={1}>
                {intl.get('upload_mission_menu_item_abort_warning_data')}
              </MDTypography>
            </div>

            <Grid
              container
              spacing={3}
              justify="center"
              style={{ marginTop: '20px' }}>
              <Grid size={6}>
                <MDButton
                  variant="gradient"
                  color="info"
                  style={{
                    color: 'white',
                    fontSize: pxToRem(18),
                    marginTop: '5px',
                    marginBottom: '15px',
                    width: '50%',
                    marginLeft: '25%',
                  }}
                  onClick={handleMissionAbortWarningModalConfirm}>
                  {intl.get('upload_mission_menu_item_abort_confirm')}
                </MDButton>
              </Grid>

              <Grid size={6}>
                <MDButton
                  variant="gradient"
                  color="error"
                  style={{
                    color: 'white',
                    fontSize: pxToRem(18),
                    marginTop: '5px',
                    marginBottom: '15px',
                    width: '50%',
                    marginLeft: '25%',
                  }}
                  onClick={handleMissionAbortWarningModalCancel}>
                  {intl.get('upload_mission_menu_item_abort_cancel')}
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </Modal>
    </Fragment>
  );
};

export default UploadMissionsMenu;
